// Service-specific copy: keep claims conditional until dispatch confirms equipment, hours and availability.
export const SERVICE_CONTENT = {
  "flatbed-towing": {
    "heading": "Flatbed towing in Richmond for vehicles that need careful handling",
    "lead": "A flatbed can be the right option when driving or conventional towing could put additional stress on a vehicle.",
    "details": "Jonny's Towing assesses the vehicle's condition, ground clearance, wheel movement and loading access before confirming the appropriate equipment. We can discuss transport for passenger cars, SUVs, lowered or premium vehicles, and non-running vehicles when safe loading is possible. The destination may be a mechanic, dealership, storage location or another authorized address.",
    "scenarios": [
      "A vehicle will not start or cannot safely be driven to a repair shop.",
      "A lowered, specialty or higher-value vehicle requires careful loading planning.",
      "A car needs to be moved from one authorized location to another without driving it."
    ],
    "prep": "Tell dispatch the year, make and model, whether the wheels roll, and whether there is collision damage, a flat tire, a locked transmission or limited clearance. Photos of the vehicle and pickup area can help us determine equipment and access requirements.",
    "faqs": [
      {
        "question": "Can you transport a car that does not run?",
        "answer": "Often, yes, depending on the vehicle's condition, access and whether it can be safely loaded. Describe any damage or locked wheels before equipment is assigned."
      },
      {
        "question": "Do you tow lowered or luxury vehicles?",
        "answer": "We assess ground clearance and loading conditions case by case. Please tell us about modifications, low front bumpers or other clearance concerns so we can discuss a safe approach."
      }
    ]
  },
  "local-towing": {
    "heading": "Local towing from Richmond to the destination you choose",
    "lead": "When your vehicle cannot continue, we help plan a practical tow without making an already stressful breakdown harder.",
    "details": "Local towing can help move an inoperable vehicle from a safe pickup point to a repair facility, home, dealership or another authorized destination. We serve Richmond and assess calls in surrounding Greater Vancouver communities based on driver and equipment availability. Dispatch will ask where the vehicle is, what happened and where it needs to go before confirming the job.",
    "scenarios": [
      "Your vehicle breaks down and is not safe to drive home.",
      "A mechanic or dealership needs the vehicle delivered for diagnosis.",
      "You need an authorized vehicle relocation and want the pickup and drop-off arranged clearly."
    ],
    "prep": "Have the pickup address or nearby intersection, destination, vehicle type, key availability and a brief description of the problem. Mention parkades, tight alleys, low clearance or restricted access. For unattended vehicles, ask what authorization and access arrangements are required.",
    "faqs": [
      {
        "question": "Can you tow my car to a repair shop?",
        "answer": "Yes, subject to availability and the receiving location's access requirements. Give dispatch the repair shop name and address so the destination can be confirmed."
      },
      {
        "question": "Can I arrange a tow if I am not standing beside the vehicle?",
        "answer": "Some moves can be arranged with clear permission, safe access and keys or other agreed arrangements. Confirm the details with dispatch before leaving the vehicle unattended."
      }
    ]
  },
  "vehicle-recovery": {
    "heading": "Vehicle recovery and winching for difficult Richmond pickups",
    "lead": "A vehicle stuck in a ditch, on a curb or in a difficult parking area needs a careful recovery plan rather than a blind pull.",
    "details": "Winching and recovery work depends on terrain, vehicle condition, attachment points, obstacles and the safe position of the recovery truck. We assess stuck vehicles in locations such as muddy areas, sloped approaches, ditches and restricted-access spaces when the operation is safe and within our equipment's capabilities. Not every situation can be confirmed from a written description alone.",
    "scenarios": [
      "A vehicle is stuck in mud, soft ground or a shallow ditch.",
      "A vehicle is hung up on a curb, uneven surface or difficult approach.",
      "A non-running or damaged vehicle cannot be reached by a straightforward tow."
    ],
    "prep": "Send dispatch the precise location, clear photos from a safe position, vehicle details and any hazards such as traffic, unstable ground or limited access. Do not stand near a tensioned winch line or attempt to attach recovery equipment yourself. We will discuss what can be safely assessed.",
    "faqs": [
      {
        "question": "Can you pull a vehicle out of a ditch or mud?",
        "answer": "Potentially. The depth, ground conditions, vehicle condition and safe truck access all matter. Photos and location details help determine whether an assessment or specialty equipment is necessary."
      },
      {
        "question": "What if my vehicle is in a tight parkade?",
        "answer": "Tell us the clearance height, access restrictions and whether the vehicle rolls. Some spaces require a different technique or equipment and must be assessed individually."
      }
    ]
  },
  "lockout-assistance": {
    "heading": "Vehicle lockout assistance in Richmond, BC",
    "lead": "Locked keys inside the car? Tell us your vehicle and location so we can assess the appropriate entry assistance.",
    "details": "Vehicle entry methods depend on the year, make, model, door and security systems. Our priority is to understand the situation and select an approach that minimizes the risk of damage. We only assist with vehicles you own or are authorized to access, and proof of ownership or authorization may be requested.",
    "scenarios": [
      "Your keys are visible inside a locked passenger vehicle.",
      "You are locked out while parked at home, work or a public location.",
      "The lock or key situation needs assessment before a safe entry method can be selected."
    ],
    "prep": "Share the vehicle year, make and model, exact location, whether a child or animal is inside, and any unusual security or damage. If anyone is at immediate risk inside a vehicle, contact emergency services rather than waiting for routine roadside assistance.",
    "faqs": [
      {
        "question": "Will lockout assistance damage my vehicle?",
        "answer": "Every vehicle and lock differs, so a damage-free result cannot be guaranteed. Dispatch can discuss the situation and whether we can offer an appropriate method."
      },
      {
        "question": "Do I need to prove the car is mine?",
        "answer": "You may be asked to show ownership, identity or another form of authorization before vehicle access is provided."
      }
    ]
  },
  "jump-starts": {
    "heading": "Car battery jump starts in Richmond",
    "lead": "A flat battery can stop the day immediately. A jump start may help, but not every no-start condition is caused by a battery.",
    "details": "Before dispatch, we ask about the symptoms, whether the engine cranks, the type of vehicle and its location. When appropriate and available, we can assist with a roadside battery boost. If the vehicle starts but continues having electrical issues, a qualified repair facility may need to inspect the battery, alternator, terminals or charging system.",
    "scenarios": [
      "The engine struggles to crank or only clicks when you try to start.",
      "The lights were left on and the battery may have discharged.",
      "A parked vehicle has a weak battery and needs an appropriate roadside assessment."
    ],
    "prep": "Tell dispatch the vehicle year, make and model, where it is parked and what happens when you turn the key or press Start. Mention hybrid or electric powertrains, unusual dashboard warnings or suspected damage; some vehicles have specific jump-starting procedures.",
    "faqs": [
      {
        "question": "Will a boost permanently fix a dead battery?",
        "answer": "Not necessarily. A boost may get the engine running, but repeated starting problems can indicate a battery or charging-system issue that needs inspection."
      },
      {
        "question": "Can you boost every vehicle?",
        "answer": "Service depends on the vehicle, electrical system and safe procedure. Provide the exact model and powertrain so dispatch can confirm whether assistance is appropriate."
      }
    ]
  },
  "tire-changes": {
    "heading": "Roadside spare tire changes in Richmond",
    "lead": "If you have a usable spare and it is safe to work where your car is parked, a roadside tire change may get you moving again.",
    "details": "We assess the road shoulder, traffic exposure, vehicle weight, wheel condition and spare-tire availability before confirming a changeover. Some locations are too dangerous for a roadside tire change and may require a tow to a safer area. This service is a spare-tire installation, not an on-site tire repair or replacement tire sale.",
    "scenarios": [
      "You find a flat tire before leaving a parking lot or driveway.",
      "A tire goes flat away from home and you have a suitable spare.",
      "A wheel needs inspection and a safe location must be confirmed before roadside work."
    ],
    "prep": "Have the vehicle year, make and model ready, and tell us whether there is an inflated spare, wheel-lock key and accessible jack point. If you are on a busy roadway or unsafe shoulder, move to safety and tell dispatch about the hazard first.",
    "faqs": [
      {
        "question": "What if I do not have a spare tire?",
        "answer": "Let dispatch know. A tow to a tire shop may be more appropriate than a roadside changeover."
      },
      {
        "question": "Can I drive normally on a temporary spare?",
        "answer": "Temporary spares often have strict speed and distance limits. Follow the instructions on the spare and in your vehicle owner's manual, and arrange a proper tire repair or replacement."
      }
    ]
  },
  "fuel-delivery": {
    "heading": "Emergency fuel delivery near Richmond",
    "lead": "Running out of fuel is frustrating. We can assess whether a small emergency supply can help you reach a fuel station safely.",
    "details": "Availability depends on the vehicle, fuel type, location and driver capacity. Tell dispatch whether the vehicle uses gasoline or diesel and whether the engine stopped unexpectedly; a no-start condition may have another cause. We will confirm the service and pricing before any dispatch. Dangerous roadside positions may call for a different response.",
    "scenarios": [
      "A gasoline vehicle ran out of fuel before reaching a station.",
      "You need help determining whether roadside fuel delivery is suitable.",
      "A vehicle stopped in a location where safe access must be assessed."
    ],
    "prep": "Share the precise location, vehicle make and model, fuel type and whether the vehicle is safely parked. Do not guess about fuel type; incorrect fuel can cause serious damage.",
    "faqs": [
      {
        "question": "Can you bring diesel as well as gasoline?",
        "answer": "Fuel type and availability must be confirmed with dispatch before service is accepted. Some vehicles need additional procedures after running completely dry."
      },
      {
        "question": "Will emergency fuel be enough to fill the tank?",
        "answer": "This service is intended for an emergency supply, not a full refill. Dispatch can explain the available quantity before you confirm."
      }
    ]
  },
  "specialty-transport": {
    "heading": "Specialty vehicle and equipment transport in Richmond",
    "lead": "Unusual cargo needs a sensible transport plan with measurements, loading details and realistic equipment requirements.",
    "details": "We assess requests involving motorcycles, classic vehicles, compact equipment, forklifts and other non-standard moves individually. Whether we can accept a job depends on dimensions, weight, condition, securement points, permits where applicable and safe access at both locations. We do not guarantee every type of load is serviceable.",
    "scenarios": [
      "A motorcycle or classic vehicle needs planned transport.",
      "Small equipment must be moved between authorized properties.",
      "A vehicle or load has unusual dimensions or access limitations."
    ],
    "prep": "Send dimensions, approximate weight, photographs, pickup and destination details, and any loading constraints. Tell us whether the item runs, rolls or has manufacturer-designated tie-down points. Dispatch will assess equipment suitability before confirming the move.",
    "faqs": [
      {
        "question": "Can you move forklifts or equipment?",
        "answer": "Some small-equipment moves may be possible after reviewing dimensions, weight, loading access and equipment availability. Share the full specifications before a quote is given."
      },
      {
        "question": "Do you transport classic or modified vehicles?",
        "answer": "We can assess those requests and discuss clearance and securement needs. Acceptance depends on the specific vehicle and transport conditions."
      }
    ]
  }
};
